import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  SimpleGrid,
  Stack,
  Button,
  List,
  ListItem,
  ListIcon,
  useColorModeValue,
  Icon,
  Flex,
  Divider,
  Image,
} from '@chakra-ui/react';
import { FaCheck, FaUserGraduate, FaArrowLeft, FaTasks, FaUsers, FaCalendar, FaFileExcel, FaClipboardList, FaChartBar, FaEnvelope } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import SEO from '../components/SEO';
import { seoData } from '../utils/seoData';

const FeatureBox = ({ title, description, icon }) => {
  return (
    <Stack
      bg={useColorModeValue('white', 'gray.800')}
      p={6}
      rounded="xl"
      shadow="md"
      transition="all 0.3s"
      _hover={{ transform: 'translateY(-3px)', shadow: 'lg' }}
    >
      <Flex
        w={12}
        h={12}
        align="center"
        justify="center"
        color="white"
        rounded="full"
        bgGradient="linear(to-br, #FF6A5F, #A63FF5)"
        mb={3}
      >
        {icon}
      </Flex>
      <Heading size="md" mb={2}>
        {title}
      </Heading>
      <Text color="gray.600" fontSize="sm">
        {description}
      </Text>
    </Stack>
  );
};

const StudentManagementProduct = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const seo = seoData.studentManagement[language] || seoData.studentManagement.en;
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const translations = {
    en: {
      backButton: 'Back to Products',
      heroTitle: 'Student Management',
      heroDescription:
        'Efficiently manage students, assign tasks, and track progress all in one centralized platform. Simplify administration and focus on what matters most - teaching.',
      heroButton: 'Get Started',
      featuresHeading: 'Complete Management Solution',
      featuresDescription: 'Everything you need to manage students and streamline administrative tasks',
      features: [
        {
          title: 'Profile Management',
          description: 'Comprehensive student profiles with contact info, performance history, and custom fields.',
          icon: FaUsers,
        },
        {
          title: 'Task Assignment',
          description: 'Easily assign tasks to individuals or groups with due dates, priorities, and notifications.',
          icon: FaTasks,
        },
        {
          title: 'Progress Monitoring',
          description: 'Track student progress in real-time with detailed completion status and performance metrics.',
          icon: FaCalendar,
        },
        {
          title: 'Bulk Operations',
          description: 'Save time with bulk actions for enrollments, assignments, and communications.',
          icon: FaUserGraduate,
        },
      ],
      benefitsHeading: 'Why Choose Student Management?',
      benefitsDescription:
        'Stop juggling multiple systems and spreadsheets. Our Student Management platform brings everything together in one intuitive, powerful solution.',
      benefitsHighlight: 'Manage Everyone',
      benefits: [
        'Centralized student database',
        'Automated enrollment workflows',
        'Parent/guardian portal access',
        'Group and class management',
        'Attendance tracking',
        'Grade book integration',
        'Communication tools (email, SMS)',
        'Custom roles and permissions',
      ],
      teacherTools: {
        title: 'Comprehensive Teacher Tools',
        description:
          'Powerful features designed to help teachers manage classes, assign work, track performance, and communicate with parents—all in one place.',
        cards: [
          {
            icon: FaFileExcel,
            title: 'Manage Classes & Students',
            description:
              'Effortlessly organize your classes and students. Upload student lists via Excel sheets and manage everything from one centralized dashboard.',
            bullets: [
              'Upload student lists with Excel sheets',
              'Organize students into classes and groups',
              'Manage student information and profiles',
            ],
            imageSrc: '/screenshots/student-management.png',
            imageAlt: 'Manage Classes and Students',
          },
          {
            icon: FaClipboardList,
            title: 'Flexible Assignment Distribution',
            description:
              'Give assignments to individual students or entire classes with just a few clicks. Customize deadlines, difficulty levels, and track completion in real-time.',
            bullets: [
              'Assign to individuals or entire classes',
              'Set custom deadlines and parameters',
              'Track assignment progress and completion',
            ],
            imageSrc: '/screenshots/student-management-assignment.png',
            imageAlt: 'Assignment Management',
          },
          {
            icon: FaChartBar,
            title: 'Performance Insights & Reporting',
            description:
              'Get detailed insights into individual and class-wide performance. Generate comprehensive reports and send them directly to parents with one click.',
            bullets: [
              'View individual student performance metrics',
              'Analyze class-wide trends and patterns',
              'Send detailed reports directly to parents',
            ],
            imageSrc: '/screenshots/student-management-student-operations.png',
            imageAlt: 'Performance Insights and Parent Communication',
          },
        ],
      },
      highlight: {
        title: 'Everything Teachers Need in One Place',
        description:
          'From managing student rosters to tracking performance and communicating with parents, our Student Management platform gives you all the tools you need to focus on what matters most—teaching.',
      },
      cta: {
        title: 'Ready to Simplify Student Management?',
        description:
          'Join institutions worldwide who trust our platform to manage their students and streamline operations.',
        primary: 'Contact Sales',
        secondary: 'View All Products',
      },
    },
    ko: {
      backButton: '제품 목록으로',
      heroTitle: '학생 관리',
      heroDescription:
        '학생 관리, 과제 배포, 성과 추적을 하나의 플랫폼에서 효율적으로 처리하세요. 행정 업무는 간소화하고 교육에 더 집중할 수 있습니다.',
      heroButton: '도입 상담하기',
      featuresHeading: '완벽한 관리 솔루션',
      featuresDescription: '학생 관리와 행정 업무를 효율화하는 모든 기능을 한곳에 모았습니다.',
      features: [
        {
          title: '프로필 관리',
          description: '연락처, 성과 이력, 사용자 정의 항목을 포함한 학생 프로필을 종합적으로 관리하세요.',
          icon: FaUsers,
        },
        {
          title: '과제 배포',
          description: '개인 또는 그룹별로 과제를 간편하게 배포하고 기한과 우선순위, 알림을 설정하세요.',
          icon: FaTasks,
        },
        {
          title: '진행 상황 모니터링',
          description: '실시간으로 학생 진행 상황을 추적하고 상세한 완료 상태와 성과를 확인하세요.',
          icon: FaCalendar,
        },
        {
          title: '대량 작업 지원',
          description: '입학 등록, 과제 배포, 커뮤니케이션을 대량으로 처리해 시간을 절약하세요.',
          icon: FaUserGraduate,
        },
      ],
      benefitsHeading: '학생 관리를 선택해야 하는 이유',
      benefitsDescription:
        '여러 시스템과 스프레드시트를 동시에 다루지 마세요. 모든 기능을 하나의 직관적이고 강력한 플랫폼에서 관리하세요.',
      benefitsHighlight: '모두를 한 곳에서 관리',
      benefits: [
        '중앙 집중식 학생 데이터베이스',
        '자동화된 등록 워크플로',
        '학부모·보호자 포털 접근',
        '그룹 및 반 관리',
        '출결 추적',
        '성적표 연동',
        '커뮤니케이션 도구 (이메일·SMS)',
        '맞춤형 역할 및 권한 설정',
      ],
      teacherTools: {
        title: '교사를 위한 통합 도구',
        description:
          '반 관리, 과제 배포, 성과 추적, 학부모 소통 등 교사가 필요한 모든 기능을 한 곳에 담았습니다.',
        cards: [
          {
            icon: FaFileExcel,
            title: '반과 학생 관리',
            description:
              'Excel로 학생 명단을 업로드하고 중앙 대시보드에서 모든 학생과 반을 손쉽게 관리하세요.',
            bullets: [
              'Excel 시트로 학생 명단 업로드',
              '반과 그룹으로 학생 정리',
              '학생 정보와 프로필 관리',
            ],
            imageSrc: '/screenshots/student-management.png',
            imageAlt: '학생 및 반 관리 화면',
          },
          {
            icon: FaClipboardList,
            title: '유연한 과제 배포',
            description:
              '개별 학생이나 반 전체에 몇 번의 클릭만으로 과제를 배포하고 마감일과 난이도를 설정할 수 있습니다.',
            bullets: [
              '개인 또는 반 전체에 과제 배포',
              '맞춤형 마감일과 설정',
              '과제 진행 상황 실시간 추적',
            ],
            imageSrc: '/screenshots/student-management-assignment.png',
            imageAlt: '과제 관리 화면',
          },
          {
            icon: FaChartBar,
            title: '성과 인사이트 및 보고',
            description:
              '개인별·반별 성과를 상세히 확인하고 종합 보고서를 생성해 학부모에게 바로 전송하세요.',
            bullets: [
              '학생 개인 성과 지표 확인',
              '반 전체의 추세와 패턴 분석',
              '학부모에게 보고서 직접 전송',
            ],
            imageSrc: '/screenshots/student-management-student-operations.png',
            imageAlt: '성과 인사이트 및 학부모 소통 화면',
          },
        ],
      },
      highlight: {
        title: '교사에게 필요한 모든 것',
        description:
          '학생 명단 관리부터 성과 추적, 학부모 소통까지, 학생 관리 플랫폼이 교사에게 필요한 모든 도구를 제공해 교육에 집중할 수 있게 합니다.',
      },
      cta: {
        title: '지금 학생 관리를 간소화하세요',
        description:
          '전 세계 교육 기관들이 우리 플랫폼으로 학생 관리와 운영을 효율화하고 있습니다.',
        primary: '영업팀에 문의하기',
        secondary: '전체 제품 보기',
      },
    },
  };

  const t = translations[language] || translations.en;

  return (
    <>
      <SEO
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        canonical="/products/student-management"
        lang={language}
        ogImage="/screenshots/student-management.png"
      />
      <Box>
        {/* Back Button */}
        <Box bg={useColorModeValue('white', 'gray.800')} py={4} borderBottom="1px" borderColor={useColorModeValue('gray.200', 'gray.700')}>
        <Container maxW="container.xl">
          <Button
            leftIcon={<FaArrowLeft />}
            variant="ghost"
            onClick={() => navigate('/products')}
            _hover={{ color: '#FF6A5F' }}
          >
            {t.backButton}
          </Button>
        </Container>
      </Box>

      {/* Hero Section */}
      <Box
        bgGradient="linear(to-br, #FF6A5F, #A63FF5, #C56CFF)"
        color="white"
        py={20}
      >
        <Container maxW="container.xl">
          <VStack spacing={8} textAlign="center">
            <Flex
              w={20}
              h={20}
              align="center"
              justify="center"
              color="white"
              rounded="full"
              bg="whiteAlpha.200"
            >
              <Icon as={FaUserGraduate} w={10} h={10} />
            </Flex>
            <Heading size="3xl" fontWeight="extrabold">
              {t.heroTitle}
            </Heading>
            <Text fontSize="xl" maxW="800px">
              {t.heroDescription}
            </Text>
            <Button
              size="lg"
              bg="white"
              color="#A63FF5"
              px={8}
              _hover={{
                bg: 'gray.100',
                transform: 'translateY(-2px)',
                shadow: 'xl',
              }}
              transition="all 0.3s"
              onClick={() => navigate('/contact')}
            >
              {t.heroButton}
            </Button>
          </VStack>
        </Container>
      </Box>

      {/* Features Section */}
      <Box py={20}>
        <Container maxW="container.xl">
          <VStack spacing={12}>
            <VStack spacing={4} textAlign="center">
              <Heading size="2xl">{t.featuresHeading}</Heading>
              <Text fontSize="lg" color="gray.600" maxW="600px">
                {t.featuresDescription}
              </Text>
            </VStack>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8} w="full">
              {t.features.map((feature, index) => (
                <FeatureBox
                  key={index}
                  title={feature.title}
                  description={feature.description}
                  icon={<Icon as={feature.icon} w={6} h={6} />}
                />
              ))}
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

      <Divider />

      {/* Benefits Section */}
      <Box py={20} bg={useColorModeValue('gray.50', 'gray.900')}>
        <Container maxW="container.xl">
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={12} alignItems="center">
            <Box
              bgGradient="linear(135deg, #FF6A5F, #A63FF5, #C56CFF, #FF8FA5)"
              h="400px"
              rounded="2xl"
              display="flex"
              alignItems="center"
              justifyContent="center"
              shadow="2xl"
            >
              <VStack spacing={4} color="white">
                <Icon as={FaUsers} w={20} h={20} />
                <Heading size="xl">{t.benefitsHighlight}</Heading>
              </VStack>
            </Box>

            <VStack align="flex-start" spacing={6}>
              <Heading size="2xl">{t.benefitsHeading}</Heading>
              <Text fontSize="lg" color="gray.600">
                {t.benefitsDescription}
              </Text>
              <List spacing={3}>
                {t.benefits.map((benefit, index) => (
                  <ListItem key={index} display="flex" alignItems="center">
                    <ListIcon as={FaCheck} color="green.500" fontSize="lg" />
                    <Text>{benefit}</Text>
                  </ListItem>
                ))}
              </List>
            </VStack>
          </SimpleGrid>
        </Container>
      </Box>

      <Divider />

      {/* Teacher Management Tools Section */}
      <Box py={20}>
        <Container maxW="container.xl">
          <VStack spacing={12}>
            <VStack spacing={4} textAlign="center">
              <Heading size="2xl">{t.teacherTools.title}</Heading>
              <Text fontSize="lg" color="gray.600" maxW="800px">
                {t.teacherTools.description}
              </Text>
            </VStack>

            {/* Teacher Tool Cards */}
            {t.teacherTools.cards.map((card, index) => (
              <SimpleGrid
                key={index}
                columns={{ base: 1, lg: 2 }}
                spacing={10}
                w="full"
                alignItems="center"
              >
                <Box order={{ base: 2, lg: index % 2 === 0 ? 1 : 2 }}>
                  <Image
                    src={card.imageSrc}
                    alt={card.imageAlt}
                    rounded="2xl"
                    shadow="2xl"
                    w="full"
                    h="auto"
                    objectFit="cover"
                    border="1px"
                    borderColor={borderColor}
                  />
                </Box>
                <VStack align="flex-start" spacing={4} order={{ base: 1, lg: index % 2 === 0 ? 2 : 1 }}>
                  <Flex
                    w={14}
                    h={14}
                    align="center"
                    justify="center"
                    color="white"
                    rounded="full"
                    bgGradient="linear(to-br, #FF6A5F, #A63FF5)"
                  >
                    <Icon as={card.icon} w={6} h={6} />
                  </Flex>
                  <Heading size="xl">{card.title}</Heading>
                  <Text fontSize="lg" color="gray.600">
                    {card.description}
                  </Text>
                  <List spacing={3}>
                    {card.bullets.map((bullet, bulletIndex) => (
                      <ListItem key={bulletIndex} display="flex" alignItems="center">
                        <ListIcon as={index < 2 ? FaCheck : (bulletIndex === 2 ? FaEnvelope : FaCheck)} color="green.500" fontSize="lg" />
                        <Text>{bullet}</Text>
                      </ListItem>
                    ))}
                  </List>
                </VStack>
              </SimpleGrid>
            ))}

            {/* Key Feature Highlight */}
            <Box
              w="full"
              bgGradient="linear(135deg, #FF6A5F, #A63FF5, #C56CFF, #FF8FA5)"
              p={10}
              rounded="2xl"
              shadow="2xl"
            >
              <VStack spacing={6} color="white">
                <Icon as={FaUserGraduate} w={16} h={16} />
                <Heading size="xl" textAlign="center">
                  {t.highlight.title}
                </Heading>
                <Text fontSize="lg" textAlign="center" maxW="700px">
                  {t.highlight.description}
                </Text>
              </VStack>
            </Box>
          </VStack>
        </Container>
      </Box>

      <Divider />

      {/* CTA Section */}
      <Box py={20} bg={useColorModeValue('gray.50', 'gray.900')}>
        <Container maxW="container.xl">
          <VStack
            spacing={6}
            bg={useColorModeValue('white', 'gray.800')}
            p={12}
            rounded="2xl"
            shadow="xl"
            textAlign="center"
          >
            <Heading size="2xl">{t.cta.title}</Heading>
            <Text fontSize="lg" color="gray.600" maxW="600px">
              {t.cta.description}
            </Text>
            <Stack direction={{ base: 'column', sm: 'row' }} spacing={4}>
              <Button
                size="lg"
                bgGradient="linear(to-r, #FF6A5F, #A63FF5)"
                color="white"
                px={8}
                _hover={{
                  bgGradient: "linear(to-r, #FF8FA5, #C56CFF)",
                  transform: 'translateY(-2px)',
                  shadow: 'xl',
                }}
                transition="all 0.3s"
                onClick={() => navigate('/contact')}
              >
                {t.cta.primary}
              </Button>
              <Button
                size="lg"
                variant="outline"
                borderColor="#A63FF5"
                color="#A63FF5"
                px={8}
                _hover={{
                  bg: '#A63FF5',
                  color: 'white',
                }}
                transition="all 0.3s"
                onClick={() => navigate('/products')}
              >
                {t.cta.secondary}
              </Button>
            </Stack>
          </VStack>
        </Container>
      </Box>
      </Box>
    </>
  );
};

export default StudentManagementProduct;

